import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import infinityLogo from "@/assets/infinity-logo.webp";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Blog", to: "/blog" },
  { label: "Resources", to: "/resources" },
];

const moreLinks = [
  { label: "Fractional CFO / FD", to: "/services/fractional-cfo-finance-director" },
  { label: "HR Services", to: "/hr-services" },
  { label: "Areas We Serve", to: "/areas" },
  { label: "MTD for ITSA", to: "/mtd-itsa" },
  { label: "Industries", to: "/industries" },
  { label: "Calculators", to: "/calculators" },
  { label: "FAQ", to: "/faq" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isMoreActive = moreLinks.some((l) => location.pathname === l.to);

  return (
    <>
      {/* Orange accent bar */}
      <div className="h-[2px] gradient-orange w-full sticky top-0 z-[51]" />

      <header
        className={cn(
          "sticky top-[2px] z-50 border-b backdrop-blur transition-all duration-300",
          scrolled
            ? "border-border bg-background shadow-lg"
            : "border-border/50 bg-background/95 supports-[backdrop-filter]:bg-background/80"
        )}
      >
        <div className="container flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={infinityLogo} alt="Infinity Financial Solutions" className="h-14 w-auto" width="92" height="56" {...{ fetchpriority: "high" }} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "relative rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-primary",
                  "after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-primary after:transition-all after:duration-300",
                  location.pathname === link.to
                    ? "text-primary after:w-3/4"
                    : "text-muted-foreground after:w-0 hover:after:w-3/4"
                )}
              >
                {link.label}
              </Link>
            ))}

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "relative flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-primary",
                    "after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-primary after:transition-all after:duration-300",
                    isMoreActive ? "text-primary after:w-3/4" : "text-muted-foreground after:w-0 hover:after:w-3/4"
                  )}
                >
                  More <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {moreLinks.map((link) => (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link
                      to={link.to}
                      className={cn(
                        "w-full",
                        location.pathname === link.to ? "text-primary" : ""
                      )}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden items-center gap-3 md:flex">
            <Link to="/contact">
              <Button variant="outline" size="sm">Contact</Button>
            </Link>
            <a href="https://infinityfinancialsolutions.portal.engager.app/" target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="gradient-orange border-0 hover-glow">Portal</Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <nav className="mt-8 flex flex-col gap-2">
                {[...navLinks, ...moreLinks].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "rounded-md px-4 py-3 text-base font-medium transition-colors hover:bg-muted",
                      location.pathname === link.to
                        ? "text-primary bg-muted"
                        : "text-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="mt-4 flex flex-col gap-2 border-t pt-4">
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full">Contact</Button>
                  </Link>
                  <a href="https://infinityfinancialsolutions.portal.engager.app/" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full gradient-orange border-0">Portal</Button>
                  </a>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
}
